// src/components/UpgradePlanDialog.jsx
import React, { useEffect, useState } from "react";
import { Dialog, Box, Typography, IconButton, Button, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle } from 'lucide-react';
import CloseIcon from "@mui/icons-material/Close";

const UPGRADE_DIALOG_SHOWN_KEY = 'upgradeDialogShown';
const SHOW_DAYS = [1,2, 3,4, 5,6,7]; 
const SHOW_HOUR = [10,12,14,16,18,20]; 

const UpgradePlanDialog = () => {
    const [user, setUser] = useState(null);
    const [userSubscription, setUserSubscription] = useState(null);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    // Fetch user profile
    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                navigate("/");
                return;
            }

            try {
                const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(response.data);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem("token");
                    navigate("/");
                }
            }
        };

        fetchProfile();
    }, [navigate]);

    // Fetch subscription based on user
    useEffect(() => {
        const fetchSubscriptions = async () => {
            try {
                const response = await axios.get("https://admin.bizstor.lk/public/api/get-subscription");

                const matchedSub = response.data.subscription
                    .filter(sub => sub.email === user?.email && sub.status === "active")
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

                setUserSubscription(matchedSub || null);
            } catch (error) {
                console.error("Failed to fetch subscriptions", error);
            }
        };

        if (user?.email) {
            fetchSubscriptions();
        }
    }, [user]);

    // Control dialog visibility based on plan, day, time, and last shown time
    useEffect(() => {
        if (userSubscription?.plan === "free" ) {
            const now = new Date();
            const currentDay = now.getDay(); // 0 (Sunday) to 6 (Saturday)
            const currentHour = now.getHours();
            const lastShownTime = localStorage.getItem(UPGRADE_DIALOG_SHOWN_KEY);
            const lastShownDate = lastShownTime ? new Date(parseInt(lastShownTime, 10)) : null;

            const shouldShowToday = SHOW_DAYS.includes(currentDay) && currentHour === SHOW_HOUR;
            const isWithin3Days = lastShownDate && (now.getTime() - lastShownDate.getTime()) < (3 * 24 * 60 * 60 * 1000);

            if (shouldShowToday && !isWithin3Days) {
                setOpen(true);
                localStorage.setItem(UPGRADE_DIALOG_SHOWN_KEY, now.getTime().toString());
            } else {
                setOpen(false);
            }
        } else {
            setOpen(false); 
        }
    }, [userSubscription]);

    const handleUpgradeNow = () => {
        navigate("/upgrade");
    };

    const features = [
        "Access to all invoices",
        "Track all your expenses",
        "Detailed financial reports",
        "Advanced inventory management",
        "Investment tracking",
        "Profit & Loss statements",
        "Sales analytics",
        "View Outstanding Payments",
    ];

    const amount = "LKR.499.00 / month";

    return (
        <Dialog open={open} onClose={() => setOpen(false)}>
            <Box sx={{ position: 'relative', px: 4, py: 3, minWidth: 350, textAlign: 'center' }}>
                <IconButton
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        '&:hover': {
                            color: 'grey'
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <AlertTriangle size={40} color="#d97706" />
                </Box>

                <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Unlock Full Access
                </Typography>

                <Typography sx={{ mb: 2 }}>
                    Your current plan has limited access. Upgrade to view outstanding payments and much more!
                </Typography>

                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: '#43a047' }}>
                    Premium Features:
                </Typography>
                <List sx={{ mb: 2 }}>
                    {features.map((feature, index) => (
                        <ListItem key={index} disablePadding>
                            <ListItemIcon sx={{ minWidth: 'auto', mr: 1, color: '#43a047' }}>
                                <CheckCircle size={16} />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                        </ListItem>
                    ))}
                </List>

                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Upgrade now to access all features starting from: <Typography variant="body1" component="span" sx={{ color: '#1976d2' }}>{amount}</Typography>
                </Typography>

                <Button variant="contained" color="primary" onClick={handleUpgradeNow}>
                    Upgrade Now
                </Button>
            </Box>
        </Dialog>
    );
};

export default UpgradePlanDialog;